import {
  IconDownload,
  IconPencil,
  IconTrash,
  IconX,
} from '@shared/ui/icons';
import { Badge } from '@shared/ui/Badge';
import { Table } from '@shared/ui/Table';
import {
  DOCUMENT_KIND_LABELS,
  DOCUMENT_KIND_ORDER,
  documentKindAnalysisHint,
  documentKindLabel,
  documentKindTone,
  isKindPending,
  isLegalKind,
  kindSourceCaption,
  kindSourceHint,
} from '@shared/lib/documentKind';
import { formatDateTime, humanSize, ROUTABLE_KIND_AGENT } from './constants';
import {
  CancelKindBtn,
  DeleteBtn,
  DownloadBtn,
  EditKindBtn,
  ExtractedCharsCell,
  FileRow,
  KindCell,
  KindEditor,
  KindMeta,
  KindSelect,
  RowActions,
  SourceCaption,
  TdRight,
  ThAction,
  ThExtracted,
  ThKind,
  ThName,
  ThSize,
  ThUploaded,
} from './styles';
import type { ContractVersionFile } from './types';

interface VersionFilesTableProps {
  items: ContractVersionFile[];
  canWrite: boolean;
  editingKindId: string | null;
  isSettingKind: boolean;
  isDeleting: boolean;
  isRouting: boolean;
  onDownload: (fileId: string, filename: string) => void;
  onDelete: (fileId: string, filename: string) => void;
  onChangeKind: (fileId: string, documentKind: string) => void;
  onRouteFile: (fileId: string, filename: string, targetAgent: string, label: string) => void;
  onStartEditKind: (fileId: string) => void;
  onCancelEditKind: () => void;
}

export function VersionFilesTable({
  items,
  canWrite,
  editingKindId,
  isSettingKind,
  isDeleting,
  isRouting,
  onDownload,
  onDelete,
  onChangeKind,
  onRouteFile,
  onStartEditKind,
  onCancelEditKind,
}: VersionFilesTableProps) {
  return (
    <Table>
      <thead>
        <tr>
          <ThName>Имя файла</ThName>
          <ThKind>Тип документа</ThKind>
          <ThSize>Размер</ThSize>
          <ThExtracted>Извлечено LLM</ThExtracted>
          <ThUploaded>Загружен</ThUploaded>
          <ThAction>Файл</ThAction>
        </tr>
      </thead>
      <tbody>
        {items.map((f) => {
          const chars = f.content_chars ?? 0;
          const attached = f.no_analysis === true;
          const isEmpty = !attached && chars === 0;

          return (
            <FileRow
              key={f.id}
              $isEmpty={isEmpty}
              onClick={() => onDownload(f.id, f.filename)}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onDownload(f.id, f.filename);
                }
              }}
              title="Скачать файл"
            >
              <td>{f.filename}</td>
              <td
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                {attached ? (
                  <Badge
                    $tone="neutral"
                    title="Приложен без переанализа: файл сохранён и скачивается, но в LLM-экспертизу не подаётся."
                  >
                    Приложен, без анализа
                  </Badge>
                ) : canWrite && editingKindId === f.id ? (
                  <KindEditor>
                    <KindSelect
                      value={f.document_kind ?? ''}
                      disabled={isSettingKind}
                      autoFocus
                      onChange={(e) => {
                        const value = e.target.value;
                        onCancelEditKind();
                        void onChangeKind(f.id, value);
                      }}
                      aria-label={`Тип документа «${f.filename}»`}
                    >
                      {!f.document_kind && (
                        <option value="" disabled>
                          {isKindPending(f.kind_source)
                            ? 'определяется…'
                            : 'не определён'}
                        </option>
                      )}
                      {DOCUMENT_KIND_ORDER.map((k) => (
                        <option key={k} value={k}>
                          {DOCUMENT_KIND_LABELS[k]}
                          {isLegalKind(k) ? ' • в анализ' : ''}
                        </option>
                      ))}
                    </KindSelect>
                    <CancelKindBtn
                      type="button"
                      onClick={onCancelEditKind}
                      aria-label="Отменить изменение типа"
                      title="Отменить"
                    >
                      <IconX size={13} />
                    </CancelKindBtn>
                  </KindEditor>
                ) : (
                  <KindCell>
                    <Badge
                      $tone={documentKindTone(f.document_kind)}
                      title={`${kindSourceHint(f.kind_source)}. ${documentKindAnalysisHint(
                        f.document_kind,
                      )}.`}
                    >
                      {isKindPending(f.kind_source) && !f.document_kind
                        ? 'Определяется…'
                        : documentKindLabel(f.document_kind)}
                      {isLegalKind(f.document_kind) ? ' • в анализ' : ''}
                    </Badge>
                    <KindMeta>
                      {!isKindPending(f.kind_source) &&
                        kindSourceCaption(f.kind_source) && (
                          <SourceCaption>
                            {kindSourceCaption(f.kind_source)}
                          </SourceCaption>
                        )}
                      {canWrite && (
                        <EditKindBtn
                          type="button"
                          onClick={() => onStartEditKind(f.id)}
                          disabled={isSettingKind}
                          aria-label={`Изменить тип документа «${f.filename}»`}
                          title="Изменить тип документа"
                        >
                          <IconPencil size={11} />
                          изменить
                        </EditKindBtn>
                      )}
                    </KindMeta>
                  </KindCell>
                )}
              </td>
              <td>{humanSize(f.size_bytes)}</td>
              <ExtractedCharsCell
                $isEmpty={isEmpty}
                $isAttached={attached}
                title={
                  attached
                    ? 'Приложен без анализа — текст не извлекается, файл в LLM не подаётся.'
                    : isEmpty
                      ? 'Файл не распарсился. LLM его не видела при анализе.'
                      : `${chars.toLocaleString('ru-RU')} символов текста извлечено в анализ`
                }
              >
                {attached
                  ? '— без анализа'
                  : isEmpty
                    ? '⚠ не извлечено'
                    : `${chars.toLocaleString('ru-RU')} симв.`}
              </ExtractedCharsCell>
              <td>{formatDateTime(f.created_at)}</td>
              <TdRight>
                <RowActions>
                  {(() => {
                    const routeInfo = f.document_kind
                      ? ROUTABLE_KIND_AGENT[f.document_kind]
                      : undefined;
                    if (!routeInfo) return null;
                    const isTechSpec =
                      f.document_kind === 'tech_spec' ||
                      f.document_kind === 'technical_spec';
                    if (f.routed_agent) {
                      return (
                        <>
                          <Badge $tone="success" title="Передано в профильный отдел">
                            В {routeInfo.label}
                          </Badge>
                          {canWrite && isTechSpec && f.routed_agent !== 'ksp' && (
                            <DownloadBtn
                              type="button"
                              disabled={isRouting}
                              onClick={(e) => {
                                e.stopPropagation();
                                void onRouteFile(f.id, f.filename, 'ksp', 'КСП');
                              }}
                              title={`Передать «${f.filename}» в ОСНОВА.КСП`}
                            >
                              В КСП
                            </DownloadBtn>
                          )}
                        </>
                      );
                    }
                    if (!canWrite) return null;
                    return (
                      <>
                        <DownloadBtn
                          type="button"
                          disabled={isRouting}
                          onClick={(e) => {
                            e.stopPropagation();
                            void onRouteFile(f.id, f.filename, routeInfo.agent, routeInfo.label);
                          }}
                          title={`Передать «${f.filename}» в ОСНОВА.${routeInfo.label}`}
                        >
                          В {routeInfo.label}
                        </DownloadBtn>
                        {isTechSpec && (
                          <DownloadBtn
                            type="button"
                            disabled={isRouting}
                            onClick={(e) => {
                              e.stopPropagation();
                              void onRouteFile(f.id, f.filename, 'ksp', 'КСП');
                            }}
                            title={`Передать «${f.filename}» в ОСНОВА.КСП`}
                          >
                            В КСП
                          </DownloadBtn>
                        )}
                      </>
                    );
                  })()}
                  <DownloadBtn
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownload(f.id, f.filename);
                    }}
                    aria-label={`Скачать «${f.filename}»`}
                    title={`Скачать «${f.filename}»`}
                  >
                    <IconDownload size={14} />
                    Скачать
                  </DownloadBtn>
                  {canWrite && (
                    <DeleteBtn
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(f.id, f.filename);
                      }}
                      disabled={isDeleting}
                      aria-label={`Удалить «${f.filename}»`}
                      title={`Удалить «${f.filename}»`}
                    >
                      <IconTrash size={14} />
                      Удалить
                    </DeleteBtn>
                  )}
                </RowActions>
              </TdRight>
            </FileRow>
          );
        })}
      </tbody>
    </Table>
  );
}
